import { useStore } from "@ludens-reklame/rubics-v3-react/store/hooks";
import {
  Card,
  ContactCard,
  FormError,
  FormField,
  FormSuccess,
} from "../styles.js";
import { StoreBuckets } from "../../../types/dataBuckets.js";
import { Button } from "../../../internal-components/Button.js";
import { Input } from "../../../internal-components/Input/Input.js";
import { DefinitionList } from "../../../internal-components/DefinitionList.js";
import { useProfile } from "@ludens-reklame/rubics-v3-react/profile/hooks";
import { useInput } from "@ludens-reklame/rubics-v3-react/util/hooks";
import { PROFILER_API_ENDPOINT } from "../../../util/constants.js";
import { CheckBox } from "../../../internal-components/Input/Checkbox.js";
import { useEffect } from "react";
import { Busy } from "../../../internal-components/Busy.js";
import { check } from "../../../icons/index.js";

interface Props {
  profilerOrganizationId?: string;
}

export const Contact: React.FC<Props> = ({ profilerOrganizationId }) => {
  const store = useStore<{}, StoreBuckets>();
  const profileInterface = useProfile();
  const profile = profileInterface.profile;
  const contactPerson =
    store.context.dataBuckets?.profileData?.contactPerson || {};
  const hasContactPersonData = Object.keys(contactPerson).length > 0;

  const { submit, setField, input, submitting, success, errored } = useInput({
    initialInput: {
      message: "",
      email: "",
      name: "",
      phone: "",
      organization: profilerOrganizationId || "",
      title: "Ny henvendelse - Min side",
      "consent:basic": false,
      "consent:email": false,
    },
    url: `${PROFILER_API_ENDPOINT}/correspondence`,
    method: "POST",
  });

  useEffect(() => {
    const { email, fullName } = profile || {};

    setField("email", email || "");
    setField("name", fullName || "");
  }, [profile?.email, profile?.fullName]);

  return (
    <>
      {contactPerson && hasContactPersonData && (
        <Card>
          <h2 className="h3">Din kontaktperson</h2>
          <ContactCard>
            {contactPerson.image && (
              <img
                src={contactPerson.image.transforms.myPage}
                alt={contactPerson.image.altText}
              />
            )}
            <div>
              <DefinitionList $horizontal $boldTitle $biggerSpace>
                <dt>Navn</dt>
                <dd>{contactPerson.name}</dd>
                <dt>E-post</dt>
                <dd>{contactPerson.email}</dd>
                <dt>Telefon</dt>
                <dd>{contactPerson.phone}</dd>
              </DefinitionList>
            </div>
          </ContactCard>
        </Card>
      )}
      <Card>
        <Busy $busy={submitting}>
          {success ? (
            <FormSuccess>
              {check}
              <h3 className="h3">Takk for din henvendelse</h3>
              <p className="b2">
                Vi vil ta kontakt med deg så snart som mulig.
              </p>
            </FormSuccess>
          ) : (
            <>
              <h2 className="h3">Send oss en henvendelse</h2>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  submit();
                }}
              >
                {profile?.email ? (
                  <input type="hidden" name="email" value={profile?.email} />
                ) : (
                  <FormField>
                    <Input
                      name="email"
                      label="E-post"
                      placeholder="E-postadresse"
                      value={input.email}
                      required={true}
                      disabled={submitting}
                      onChange={(e) => {
                        setField("email", e.target.value);
                      }}
                    />
                  </FormField>
                )}
                <FormField>
                  <Input
                    textarea
                    name="message"
                    label="Hva gjelder din henvendelse?"
                    placeholder="Beskriv hva du ønsker hjelp med"
                    value={input.message}
                    required={true}
                    disabled={submitting}
                    onChange={(e) => {
                      setField("message", e.target.value);
                    }}
                  />
                </FormField>
                <FormField>
                  <CheckBox
                    label={`* Jeg godtar at ${store.site.name} behandler mine personopplysninger i tråd med personvernerklæringen`}
                    hasRichLabel={true}
                    name="consent:basic"
                    value={input["consent:basic"]}
                    checked={input["consent:basic"]}
                    required={true}
                    disabled={submitting}
                    onChange={() => {
                      setField("consent:basic", !input["consent:basic"]);
                    }}
                  />
                </FormField>
                <FormField $last>
                  <CheckBox
                    label={`Ja, jeg godkjenner at ${store.site.name} kan lagre min informasjon og sende meg markedsføring på e-post.`}
                    hasRichLabel={true}
                    name="consent:email"
                    value={input["consent:email"]}
                    checked={input["consent:email"]}
                    disabled={submitting}
                    onChange={() => {
                      setField("consent:email", !input["consent:email"]);
                    }}
                  />
                </FormField>
                {errored && (
                  <FormError>
                    <p className="h6">
                      Noe gikk galt i innsending av skjema. Vennligst prøv på
                      nytt.
                    </p>
                  </FormError>
                )}
                <Button type="submit" disabled={submitting}>
                  {submitting ? "Sender inn" : "Send inn"}
                </Button>
              </form>
            </>
          )}
        </Busy>
      </Card>
    </>
  );
};
